// --- src/components/Settings.tsx ---
import React, { useState, useEffect } from 'react';
import { InventoryMap, SupplierMap } from './types';
import { supabase } from '@/lib/supabaseClient';
import { supabaseService } from '../services/supabaseService';
import {
  LogOut,
  Trash2,
  User,
  RefreshCw,
  MessageSquare,
  CheckCircle,
  Clock,
  Shield,
  Users,
} from 'lucide-react';
import { AppView } from './types';
import { toast } from 'react-hot-toast';
import { ConfirmDialog } from './ConfirmDialog';
import { getUserFriendlyError } from '../utils/errorHelper';

interface SettingsProps {
  inventoryData: InventoryMap;
  supplierData: SupplierMap;
  setInventoryData: (data: InventoryMap) => void;
  setSupplierData: (data: SupplierMap) => void;
  isAdmin: boolean;
  setView: (view: AppView) => void;
}

const Settings: React.FC<SettingsProps> = ({
  setInventoryData,
  setSupplierData,
  isAdmin,
  setView,
}) => {
  const [displayName, setDisplayName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState<any[]>([]); // Itt tároljuk a jegyeket

  useEffect(() => {
    // User infó betöltése
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) {
        const metaName = data.user.user_metadata?.username;
        const emailName = data.user.email?.split('@')[0];
        setDisplayName(metaName || emailName || 'Felhasználó');
      }
    });

    // Jegyek betöltése
    supabaseService.getMyTickets().then(setTickets);

    // Debug: Log admin status
    console.log('Settings component - isAdmin prop:', isAdmin);
  }, [isAdmin]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    // TASK 1: Clear cached user on logout
    localStorage.removeItem('offline_user');
    console.log('[Settings] Cleared cached user session');
    window.location.reload();
  };

  const [clearConfirm, setClearConfirm] = useState(false);

  const handleClearInventory = () => {
    setClearConfirm(true);
  };

  const handleClearConfirm = async () => {
    setLoading(true);
    try {
      await supabaseService.clearInventoryValues();
      setInventoryData({});
      toast.success('A leltár sikeresen lenullázva.');
      setClearConfirm(false);
    } catch (error: any) {
      const friendlyError = getUserFriendlyError(error);
      toast.error(friendlyError);
    } finally {
      setLoading(false);
    }
  };

  const handleForceReload = async () => {
    setLoading(true);
    try {
      const { inventoryMap, supplierMap } = await supabaseService.fetchAllData();
      setInventoryData(inventoryMap);
      setSupplierData(supplierMap);
      alert('Adatok frissítve a felhőből!');
    } catch (e) {
      alert('Hiba a frissítéskor.');
    } finally {
      setLoading(false);
    }
  };

  // Státusz színező segédfüggvény
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'OPEN':
        return (
          <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-1 rounded-full flex items-center gap-1">
            <Clock size={12} /> Függőben
          </span>
        );
      case 'IN_PROGRESS':
        return (
          <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full flex items-center gap-1">
            <RefreshCw size={12} /> Folyamatban
          </span>
        );
      case 'CLOSED':
        return (
          <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full flex items-center gap-1">
            <CheckCircle size={12} /> Megoldva
          </span>
        );
      default:
        return (
          <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full">
            Ismeretlen
          </span>
        );
    }
  };

  return (
    <div
      className="max-w-2xl mx-auto p-6 space-y-8 pb-20 min-h-screen"
      style={{ backgroundColor: 'var(--color-background, #f9fafb)' }}
    >
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <User className="text-primary" />
        Fiók és Beállítások
      </h2>

      {/* Felhasználói Fiók */}
      <div className="bg-card p-6 rounded-xl shadow-sm">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Bejelentkezett Fiók</h3>
        <div className="flex items-center justify-between bg-white dark:bg-gray-700 p-4 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold uppercase">
              {displayName.charAt(0)}
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{displayName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Szinkronizáció aktív</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium flex items-center gap-1"
          >
            <LogOut size={16} /> Kilépés
          </button>
        </div>
      </div>

      {/* HIBAJEGYEK LISTÁJA - EZ AZ ÚJ RÉSZ */}
      {tickets.length > 0 && (
        <div className="bg-card p-6 rounded-xl shadow-sm">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <MessageSquare size={18} className="text-gray-400 dark:text-gray-500" />
            Beküldött Hibajegyek
          </h3>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {tickets.map((ticket) => (
              <div key={ticket.id} className="py-3">
                <div className="flex justify-between items-start mb-1">
                  <span className="font-medium text-gray-900 dark:text-white text-sm">
                    {ticket.description.substring(0, 50)}...
                  </span>
                  {getStatusBadge(ticket.status)}
                </div>
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    {ticket.page} • {ticket.process}
                  </span>
                  <span>{new Date(ticket.created_at).toLocaleDateString('hu-HU')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Adminisztráció - Only visible to admins */}
      {isAdmin && (
        <div className="bg-card p-6 rounded-xl shadow-sm space-y-4">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="text-purple-600 dark:text-purple-400" size={20} />
            Adminisztráció
          </h3>
          <button
            onClick={() => setView(AppView.USER_MANAGEMENT)}
            className="w-full flex items-center justify-center gap-2 p-4 border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition"
          >
            <Users size={20} />
            Munkatársak és Jogosultságok
          </button>
        </div>
      )}

      {/* Adatkezelés */}
      <div className="bg-card p-6 rounded-xl shadow-sm space-y-4">
        <h3 className="font-semibold text-gray-900 dark:text-white">Adatkezelés</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <button
            onClick={handleForceReload}
            disabled={loading}
            className="flex items-center justify-center gap-2 p-4 border border-primary/30 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            Adatok Újratöltése
          </button>
          <button
            onClick={handleClearInventory}
            disabled={loading}
            className="flex items-center justify-center gap-2 p-4 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition"
          >
            <Trash2 size={20} />
            Új Leltár (Nullázás)
          </button>
        </div>
      </div>

      <div className="text-center text-xs text-gray-400 dark:text-gray-500 mt-10">
        Solyom barkács | LeltárMester AI v 2.0
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={clearConfirm}
        title="Leltár törlése"
        message="BIZTOSAN TÖRLÖD A LELTÁRT?\n\nEz lenulláz minden darabszámot a felhőben. A terméknevek megmaradnak."
        variant="danger"
        confirmText="Törlés"
        cancelText="Mégse"
        onConfirm={handleClearConfirm}
        onCancel={() => setClearConfirm(false)}
      />
    </div>
  );
};

export default Settings;
